import { useMemo } from 'react'
import regulatoryData from '../../../shared/data/regulatoryUpdates.json'
import benchmarkData from '../../../shared/data/industryBenchmarks.json'
import threatData from '../../../shared/data/threatProfiles.json'

const STALENESS_THRESHOLDS = {
  regulatory: 10,   // days — weekly schedule + buffer
  benchmarks: 180,  // days — ~6 months
  threatProfiles: 180 // days — ~6 months
}

function daysSince(dateStr) {
  if (!dateStr) return null
  const then = new Date(dateStr)
  if (isNaN(then)) return null
  return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24))
}

export default function useDataStaleness() {
  return useMemo(() => {
    const alerts = []

    // Regulatory updates
    const regDays = daysSince(regulatoryData.lastChecked)
    if (regDays === null) {
      alerts.push({
        id: 'regulatory-no-timestamp',
        source: 'Regulatory Updates',
        severity: 'warning',
        message: 'No last-checked timestamp found. Run the regulatory monitoring script.',
        lastUpdated: null,
        daysStale: null,
        file: 'client/src/shared/data/regulatoryUpdates.json',
        action: 'Run: node scripts/regulatory-check.mjs',
        details: 'The automated weekly script scans for new regulatory changes (penalties, mandates, guidance) across all tracked industries and updates the findings array with severity, affected industries, and platform actions.'
      })
    } else if (regDays > STALENESS_THRESHOLDS.regulatory) {
      alerts.push({
        id: 'regulatory-stale',
        source: 'Regulatory Updates',
        severity: regDays > STALENESS_THRESHOLDS.regulatory * 2 ? 'critical' : 'warning',
        message: `Regulatory data was last checked ${regDays} days ago (threshold: ${STALENESS_THRESHOLDS.regulatory} days).`,
        lastUpdated: regulatoryData.lastChecked,
        daysStale: regDays,
        file: 'client/src/shared/data/regulatoryUpdates.json',
        action: 'Run: node scripts/regulatory-check.mjs',
        details: 'The automated weekly script scans for new regulatory changes (penalties, mandates, guidance) across all tracked industries and updates the findings array with severity, affected industries, and platform actions.'
      })
    }

    // Industry benchmarks
    const benchDays = daysSince(benchmarkData.metadata?.lastUpdated)
    const benchmarkIndustries = Object.keys(benchmarkData.benchmarks || {})
    if (benchDays === null) {
      alerts.push({
        id: 'benchmarks-no-timestamp',
        source: 'Industry Benchmarks',
        severity: 'warning',
        message: 'No last-updated timestamp found for industry benchmark data.',
        lastUpdated: null,
        daysStale: null,
        file: 'client/src/shared/data/industryBenchmarks.json',
        action: 'Manual update required',
        details: `Review and update maturity benchmark scores for ${benchmarkIndustries.length} industry verticals. For each vertical, collect: maturityBenchmark (median score 0-100), benchmarkRange (25th-75th percentile low/high), regulatoryPressure level, and cite a primary source. Key sources to check: Wavestone Cyber Benchmark (annual, Jan), KLAS Healthcare Cybersecurity study, Picus Security sector reports, and any new NIST CSF adoption surveys. Update metadata.lastUpdated and metadata.nextReviewDate after changes.`,
        collectFields: [
          'maturityBenchmark — median maturity score (0-100) for mid-market orgs',
          'benchmarkRange.low / high — 25th to 75th percentile range',
          'regulatoryPressure — Low, Medium, High, or Very High',
          'sourceNotes — cite which report supports the number',
          'primarySource — add new source entries to the sources array'
        ]
      })
    } else if (benchDays > STALENESS_THRESHOLDS.benchmarks) {
      alerts.push({
        id: 'benchmarks-stale',
        source: 'Industry Benchmarks',
        severity: benchDays > STALENESS_THRESHOLDS.benchmarks * 2 ? 'critical' : 'warning',
        message: `Industry benchmark data was last updated ${benchDays} days ago (threshold: ${STALENESS_THRESHOLDS.benchmarks} days).`,
        lastUpdated: benchmarkData.metadata.lastUpdated,
        daysStale: benchDays,
        file: 'client/src/shared/data/industryBenchmarks.json',
        action: 'Manual update required',
        details: `Review and update maturity benchmark scores for ${benchmarkIndustries.length} industry verticals (${benchmarkIndustries.join(', ')}). For each vertical, collect: maturityBenchmark (median score 0-100), benchmarkRange (25th-75th percentile low/high), regulatoryPressure level, and cite a primary source. Key sources to check: Wavestone Cyber Benchmark (annual, Jan), KLAS Healthcare Cybersecurity study, Picus Security sector reports, and any new NIST CSF adoption surveys. Update metadata.lastUpdated and metadata.nextReviewDate after changes.`,
        collectFields: [
          'maturityBenchmark — median maturity score (0-100) for mid-market orgs',
          'benchmarkRange.low / high — 25th to 75th percentile range',
          'regulatoryPressure — Low, Medium, High, or Very High',
          'sourceNotes — cite which report supports the number',
          'primarySource — add new source entries to the sources array'
        ]
      })
    }

    // Threat profiles
    const threatDays = daysSince(threatData.metadata?.lastUpdated)
    const threatVerticals = (threatData.profiles || []).map(p => p.verticalName)
    if (threatDays === null) {
      alerts.push({
        id: 'threat-profiles-no-timestamp',
        source: 'Threat Profiles',
        severity: 'warning',
        message: 'No last-updated timestamp found. Run the threat profile refresh script.',
        lastUpdated: null,
        daysStale: null,
        file: 'client/src/shared/data/threatProfiles.json',
        action: 'Run: node scripts/threat-profile-refresh.mjs',
        details: `Pulls CISA KEV and MITRE ATT&CK data, then uses Claude to update threat actor likelihood scores, attack vector prevalence, and target asset criticality across ${threatVerticals.length} verticals (${threatVerticals.join(', ')}).`,
        collectFields: [
          'threatActors — name, likelihood (0-1), description per vertical',
          'attackVectors — name, prevalence (0-1), description per vertical',
          'targetAssets — name, criticality (0-1), description per vertical'
        ]
      })
    } else if (threatDays > STALENESS_THRESHOLDS.threatProfiles) {
      alerts.push({
        id: 'threat-profiles-stale',
        source: 'Threat Profiles',
        severity: threatDays > STALENESS_THRESHOLDS.threatProfiles * 2 ? 'critical' : 'warning',
        message: `Threat profile data was last updated ${threatDays} days ago (threshold: ${STALENESS_THRESHOLDS.threatProfiles} days).`,
        lastUpdated: threatData.metadata.lastUpdated,
        daysStale: threatDays,
        file: 'client/src/shared/data/threatProfiles.json',
        action: 'Run: node scripts/threat-profile-refresh.mjs',
        details: `Pulls CISA KEV and MITRE ATT&CK data, then uses Claude to update threat actor likelihood scores, attack vector prevalence, and target asset criticality across ${threatVerticals.length} verticals (${threatVerticals.join(', ')}).`,
        collectFields: [
          'threatActors — name, likelihood (0-1), description per vertical',
          'attackVectors — name, prevalence (0-1), description per vertical',
          'targetAssets — name, criticality (0-1), description per vertical'
        ]
      })
    }

    return {
      alerts,
      count: alerts.filter(a => a.severity !== 'info').length,
      totalCount: alerts.length
    }
  }, [])
}
